import React from 'react'
import { motion } from 'framer-motion'
import { Brain, TrendingUp, Users, Leaf, ArrowRight, FileText } from 'lucide-react'
import { Section, Card, Button, Badge } from '../components/ui'
import { cn } from '../utils'

const researchAreas = [
  {
    icon: Brain,
    title: 'Artificial Intelligence',
    description: 'Developing AI solutions tailored to African contexts, from healthcare diagnostics to agricultural optimization. Our research focuses on making AI accessible and relevant to local needs.',
    topics: ['Healthcare AI', 'AgriTech', 'NLP for African Languages'],
    status: 'active',
  },
  {
    icon: TrendingUp,
    title: 'Digital Economy',
    description: 'Researching pathways for digital inclusion and economic empowerment across Africa. We study fintech adoption, digital infrastructure, and e-commerce ecosystems.',
    topics: ['Fintech', 'Digital Infrastructure', 'E-commerce'],
    status: 'active',
  },
  {
    icon: Users,
    title: 'Human Capital Development',
    description: 'Analyzing workforce development strategies to maximize Africa\'s demographic dividend. Our work informs education policy and skills training initiatives.',
    topics: ['Skills Gap Analysis', 'Education Policy', 'Youth Employment'],
    status: 'active',
  },
  {
    icon: Leaf,
    title: 'Sustainable Development',
    description: 'Exploring technology-driven approaches to environmental sustainability and climate resilience. We focus on solutions that balance growth with environmental stewardship.',
    topics: ['Climate Tech', 'Green Energy', 'Sustainable Agriculture'],
    status: 'upcoming',
  },
]

const publications = [
  {
    title: 'The Future of Work in Africa',
    type: 'Report',
    date: '2024',
    description: 'A comprehensive analysis of labor market trends and the skills needed for the future.',
  },
  {
    title: 'AI Readiness Assessment: African Markets',
    type: 'Working Paper',
    date: '2024',
    description: 'Evaluating the infrastructure and human capital requirements for AI adoption.',
  },
  {
    title: 'Digital Financial Inclusion: Lessons from Mobile Money',
    type: 'Policy Brief',
    date: '2023',
    description: 'Insights on expanding financial services through digital channels.',
  },
]

const Research: React.FC = () => {
  return (
    <>
      {/* Hero */}
      <Section background="muted" padding="xl" className="pt-32 md:pt-40">
        <motion.div
          className="max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-sm font-medium text-primary-500 dark:text-primary-400 uppercase tracking-wider mb-4">
            Research
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 dark:text-white mb-6">
            Evidence-Based Insights for Africa
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            Our research combines rigorous methodology with deep local knowledge
            to produce actionable insights that drive positive change across the continent.
          </p>
        </motion.div>
      </Section>

      {/* Research Areas */}
      <Section background="default" padding="lg">
        <Section.Header
          subtitle="Focus Areas"
          title="What We Study"
          description="Our research spans four interconnected domains that together address the most pressing challenges and opportunities facing Africa."
          align="center"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {researchAreas.map((area, index) => {
            const Icon = area.icon
            return (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card variant="default" padding="lg" className="h-full">
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      'w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center',
                      'bg-primary-50 dark:bg-primary-900/30',
                      'text-primary-500 dark:text-primary-400'
                    )}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {area.title}
                        </h3>
                        {area.status === 'upcoming' && (
                          <Badge variant="outline" size="sm">Coming Soon</Badge>
                        )}
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4">
                        {area.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {area.topics.map((topic) => (
                          <Badge key={topic} variant="default" size="sm">
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )
          })}
        </div>
      </Section>

      {/* Publications */}
      <Section background="muted" padding="lg">
        <Section.Header
          subtitle="Publications"
          title="Our Latest Work"
          description="Access our reports, working papers, and policy briefs."
          align="center"
        />

        <div className="max-w-3xl mx-auto space-y-4">
          {publications.map((pub, index) => (
            <motion.div
              key={pub.title}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <Card
                variant="default"
                padding="md"
                hover
                className="group cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className={cn(
                    'w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center',
                    'bg-gray-100 dark:bg-gray-800',
                    'text-gray-500 dark:text-gray-400',
                    'group-hover:bg-primary-50 dark:group-hover:bg-primary-900/30',
                    'group-hover:text-primary-500 dark:group-hover:text-primary-400',
                    'transition-colors duration-200'
                  )}>
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="primary" size="sm">{pub.type}</Badge>
                      <span className="text-xs text-gray-500 dark:text-gray-500">{pub.date}</span>
                    </div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors">
                      {pub.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {pub.description}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary-500 dark:group-hover:text-primary-400 group-hover:translate-x-1 transition-all flex-shrink-0" />
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            More publications coming soon as our research programs expand.
          </p>
          <Button as="link" to="/contact" variant="outline">
            Request Research Access
          </Button>
        </motion.div>
      </Section>

      {/* Methodology */}
      <Section background="default" padding="lg">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm font-medium text-primary-500 dark:text-primary-400 uppercase tracking-wider mb-4">
            Our Approach
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-6">
            Rigorous & Relevant
          </h2>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-8">
            Our research methodology combines quantitative analysis with qualitative insights
            from local experts and stakeholders. We believe that impactful research must be
            both methodologically sound and deeply rooted in local context.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
            {[
              { number: '01', title: 'Identify', desc: 'Partner with local stakeholders to identify pressing challenges' },
              { number: '02', title: 'Research', desc: 'Apply rigorous methods to gather and analyze data' },
              { number: '03', title: 'Act', desc: 'Translate findings into actionable recommendations' },
            ].map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card variant="ghost" padding="md">
                  <span className="text-4xl font-bold text-primary-200 dark:text-primary-800">
                    {step.number}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mt-2 mb-1">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {step.desc}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>
    </>
  )
}

export default Research
